'use client';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  fetchTomVendingSiteVisitHistoryByRange,
  type TomVendingSiteVisitHistoryRecentRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';

const PAGE_SIZE = 25;

function defaultWideDateRange() {
  const to = new Date();
  return {
    dateFrom: '2020-01-01',
    dateTo: to.toISOString().slice(0, 10),
  };
}

const TomsVendingAllSiteVisitsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const init = useMemo(() => defaultWideDateRange(), []);
  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [rows, setRows] = useState<TomVendingSiteVisitHistoryRecentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(
    async (skip: number, append: boolean) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const r = await fetchTomVendingSiteVisitHistoryByRange({
          dateFrom,
          dateTo,
          skip,
          take: PAGE_SIZE,
        });
        setTotal(r.total);
        setRows((prev) => (append ? [...prev, ...r.data] : r.data));
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to load site visits');
        if (!append) {
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (append) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [dateFrom, dateTo, showError],
  );

  useEffect(() => {
    void fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          rows.length < total &&
          !loading &&
          !loadingMoreRef.current
        ) {
          void fetchPage(rows.length, true);
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows.length, total, loading, fetchPage]);

  const openVisit = (row: TomVendingSiteVisitHistoryRecentRow) => {
    navigate(
      `/app/toms-vending/site-visits/${row.siteVisitId}?locationId=${row.locationTomVendingId}`,
    );
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'All site visits' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="All site visits" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Newest first across all locations. Narrow by visit date range if you want.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No visits in this date range. Try widening the range or record money collected or saved
            tallies during a site visit.
          </Typography>
        ) : (
          <>
            {total > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Showing {rows.length} of {total} visits.
              </Typography>
            )}
            <List disablePadding>
              {rows.map((row) => (
                <ListItemButton
                  key={row.siteVisitId}
                  onClick={() => openVisit(row)}
                  sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
                >
                  <ListItemText
                    primary={row.locationName?.trim() || `Location #${row.locationTomVendingId}`}
                    secondary={
                      <>
                        {formatVisitDateLabel(row.visitDate)}
                        {row.userDisplayName ? ` · ${row.userDisplayName}` : ''}
                        {` · ${row.machineSnapshotCount} machine${row.machineSnapshotCount === 1 ? '' : 's'}`}
                        {(row.totalCashCollectedCents ?? 0) > 0
                          ? ` · Cash ${formatUsdFromCents(row.totalCashCollectedCents ?? 0)}`
                          : ''}
                      </>
                    }
                  />
                  <ChevronRightIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
            </List>
            <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2, minHeight: 24 }}>
              {loadingMore && <CircularProgress size={24} />}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TomsVendingAllSiteVisitsPage;
